
import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const WhitepaperPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    propertyLocation: '',
    currentRevenue: '',
    propertyType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/submit/whitepaper-download', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitMessage('Success! Check your email for the white paper download link and exclusive bonus materials.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          propertyLocation: '',
          currentRevenue: '',
          propertyType: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      setSubmitMessage('Error: Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.firstName && formData.lastName && formData.email && 
    formData.propertyLocation && formData.currentRevenue && formData.propertyType;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  FREE RESEARCH REPORT
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  The $2,400 Photo: How Professional Airbnb Photography Increases Annual Revenue
                </h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Discover the data-driven insights from analyzing 500+ Airbnb properties that reveal 
                  exactly how professional photography impacts booking rates, nightly rates, and annual revenue.
                </p>
                
                <div className="bg-white rounded-lg p-6 shadow-lg border border-blue-100 mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">What You'll Learn:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">The exact percentage increase in bookings after professional photos (spoiler: it's 73% on average)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">How photo quality affects nightly rates and guest willingness to pay premium prices</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">The psychology behind guest booking decisions in the first 3 seconds</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">ROI timeline: When you'll see payback on your photo investment</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">Market analysis by property type and geographic location</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="lg:pl-8">
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Download Your Free Copy</h2>
                    <p className="text-gray-600">Get instant access to the complete 12-page research report</p>
                  </div>

                  <form onSubmit={handleSubmit} data-readdy-form id="whitepaper-download">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                          First Name *
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="Smith"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="john@email.com"
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="propertyLocation" className="block text-sm font-medium text-gray-700 mb-2">
                        Property Location *
                      </label>
                      <input
                        type="text"
                        id="propertyLocation"
                        name="propertyLocation"
                        value={formData.propertyLocation}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="City, State"
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 mb-2">
                        Property Type *
                      </label>
                      <select
                        id="propertyType"
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select property type</option>
                        <option value="apartment">Apartment/Condo</option>
                        <option value="house">Single Family Home</option>
                        <option value="townhouse">Townhouse</option>
                        <option value="villa">Villa/Luxury Home</option>
                        <option value="cabin">Cabin/Cottage</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="mb-6">
                      <label htmlFor="currentRevenue" className="block text-sm font-medium text-gray-700 mb-2">
                        Current Monthly Revenue *
                      </label>
                      <select
                        id="currentRevenue"
                        name="currentRevenue"
                        value={formData.currentRevenue}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                      >
                        <option value="">Select revenue range</option>
                        <option value="under-1000">Under $1,000</option>
                        <option value="1000-2500">$1,000 - $2,500</option>
                        <option value="2500-5000">$2,500 - $5,000</option>
                        <option value="5000-10000">$5,000 - $10,000</option>
                        <option value="over-10000">Over $10,000</option>
                      </select>
                    </div>

                    {submitMessage && (
                      <div className={`p-4 rounded-lg mb-6 ${
                        submitMessage.includes('Success')
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={!isFormValid || isSubmitting}
                      className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Sending Download Link...
                        </span>
                      ) : (
                        'Get Free White Paper Now'
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center mt-4">
                      We respect your privacy. Unsubscribe at any time.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Summary Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Executive Summary</h2>
              <p className="text-xl text-gray-600">Key findings from our comprehensive analysis</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              <div className="text-center bg-blue-50 rounded-xl p-6">
                <div className="text-4xl font-bold text-blue-600 mb-2">73%</div>
                <div className="text-sm text-gray-600">Average booking increase after professional photos</div>
              </div>
              <div className="text-center bg-green-50 rounded-xl p-6">
                <div className="text-4xl font-bold text-green-600 mb-2">$2,400</div>
                <div className="text-sm text-gray-600">Average annual revenue increase per property</div>
              </div>
              <div className="text-center bg-purple-50 rounded-xl p-6">
                <div className="text-4xl font-bold text-purple-600 mb-2">18%</div>
                <div className="text-sm text-gray-600">Average nightly rate increase achievable</div>
              </div>
              <div className="text-center bg-amber-50 rounded-xl p-6">
                <div className="text-4xl font-bold text-amber-600 mb-2">45</div>
                <div className="text-sm text-gray-600">Days average ROI payback period</div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Research Methodology</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Data Collection</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>500+ Airbnb properties analyzed</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>12-month performance tracking</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Before/after photo comparison</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Guest survey responses</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Analysis Framework</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Booking rate correlation analysis</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Revenue impact measurement</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Market segmentation by property type</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                      <span>Geographic performance variations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Insights Preview */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Insights Preview</h2>
              <p className="text-xl text-gray-600">A glimpse into the comprehensive findings</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-eye-line text-blue-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">The 3-Second Rule</h3>
                <p className="text-gray-600 mb-4">
                  Our research reveals that 87% of potential guests make their initial decision within 3 seconds of viewing the first photo.
                </p>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-blue-800 font-semibold">Full Report Includes:</div>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1">
                    <li>• Eye-tracking study results</li>
                    <li>• Color psychology impact data</li>
                    <li>• Composition effectiveness metrics</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-money-dollar-circle-line text-green-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Revenue Multiplication</h3>
                <p className="text-gray-600 mb-4">
                  Properties with professional photos command 18% higher nightly rates and achieve 73% more bookings on average.
                </p>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm text-green-800 font-semibold">Full Report Includes:</div>
                  <ul className="text-sm text-green-700 mt-2 space-y-1">
                    <li>• ROI calculations by property type</li>
                    <li>• Seasonal impact variations</li>
                    <li>• Market positioning strategies</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-calendar-check-line text-purple-600 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Occupancy Optimization</h3>
                <p className="text-gray-600 mb-4">
                  Professional photography reduces vacancy periods by an average of 12 days per month, significantly improving cash flow.
                </p>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-sm text-purple-800 font-semibold">Full Report Includes:</div>
                  <ul className="text-sm text-purple-700 mt-2 space-y-1">
                    <li>• Booking velocity analysis</li>
                    <li>• Guest retention correlation</li>
                    <li>• Review score improvements</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Trusted by Industry Leaders</h2>
            <p className="text-xl text-gray-600 mb-12">Property managers and hosts worldwide rely on our research</p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="text-4xl font-bold text-blue-600 mb-2">2,500+</div>
                <div className="text-gray-600">Properties Analyzed</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="text-4xl font-bold text-green-600 mb-2">15M+</div>
                <div className="text-gray-600">Revenue Data Points</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="text-4xl font-bold text-purple-600 mb-2">98%</div>
                <div className="text-gray-600">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Airbnb Revenue?</h2>
            <p className="text-xl mb-8 opacity-90">
              Download the complete white paper and discover the exact strategies that have helped thousands of hosts increase their revenue.
            </p>
            <a 
              href="#whitepaper-download"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('whitepaper-download')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Your Free Copy Now
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Research Report Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Report",
          "name": "The $2,400 Photo: How Professional Airbnb Photography Increases Annual Revenue",
          "description": "Data-driven insights from analyzing 500+ Airbnb properties revealing how professional photography impacts booking rates and revenue",
          "author": {
            "@type": "Organization",
            "name": "VistaBoost Imaging",
            "url": "https://vistaboost-imaging.com"
          },
          "datePublished": "2024-01-15",
          "abstract": "Comprehensive analysis of 500+ Airbnb properties showing 73% average booking increase and $2,400 annual revenue boost from professional photography",
          "keywords": ["Airbnb photography", "vacation rental revenue", "professional photos", "booking rates", "property photography ROI"],
          "about": {
            "@type": "Thing",
            "name": "Airbnb Photography Impact on Revenue"
          },
          "mainEntity": {
            "@type": "Dataset",
            "name": "Airbnb Photo Performance Analysis",
            "description": "12-month performance tracking of 500+ properties with before/after photo comparison",
            "measurementTechnique": "Booking rate correlation analysis and revenue impact measurement"
          }
        })}
      </script>

      {/* Offer Schema for Free Download */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Offer",
          "name": "Free Airbnb Photography Research Report",
          "description": "Download the complete 12-page research report on how professional photography increases Airbnb revenue",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "DigitalDocument",
            "name": "The $2,400 Photo Research Report",
            "encodingFormat": "application/pdf"
          },
          "seller": {
            "@type": "Organization",
            "name": "VistaBoost Imaging"
          }
        })}
      </script>
    </div>
  );
};

export default WhitepaperPage;
